/**
 * GroupCartProvider — single source of truth for the active group cart.
 *
 * Lifecycle:
 *   1. On mount, hydrate from sessionStorage (if a participant token from a
 *      prior page-load is still valid the API call resolves and we land in
 *      `active`; otherwise the storage entry is cleared).
 *   2. While `active`, an SSE connection to /v1/group-carts/{id}/events keeps
 *      the cart in sync. The first `snapshot` frame replaces the cart;
 *      subsequent typed deltas are reduced into draftLines / participants.
 *   3. Mutations call the API client with the current `version` (OCC). If a
 *      412 comes back the conflict carries `{ currentVersion, cart }` —
 *      we re-base local state and surface the error so the caller can retry.
 *   4. Firing the cart transitions to `fired` and emits the orderId — the
 *      consuming screen routes to /dine-in-order/{orderId}.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from "react";

import { SSE_BASE, getApiKey } from "@/api/http";
import {
  groupCartApi,
  GroupCartVersionConflict,
  type AddLineInput,
  type GroupCart,
  type GroupCartParticipant,
  type UpdateLinePatch,
} from "@/api/group-cart";
import {
  deviceFingerprint,
  readParticipant,
  writeParticipant,
  type StoredParticipant,
} from "@/lib/participant-token";
import { openSSE } from "@/lib/sse-client";
import {
  trackGroupCartAbandoned,
  trackGroupCartCreated,
  trackGroupCartFired,
  trackGroupCartJoined,
} from "@/lib/analytics-events";
import { canRefire, withVersionRetry } from "@/lib/group-version-retry";
import { latestWins } from "@/lib/latest-wins";
import { useTable } from "./table";

export type GroupCartStatus =
  | "idle"
  | "creating"
  | "joining"
  | "active"
  | "fired"
  | "abandoned"
  | "closed"
  | "error";

interface State {
  status: GroupCartStatus;
  cart: GroupCart | null;
  participant: StoredParticipant | null;
  error: string | null;
}

type Action =
  | { type: "reset" }
  | { type: "set_status"; status: GroupCartStatus }
  | { type: "set_cart"; cart: GroupCart; participant?: StoredParticipant }
  | { type: "patch_participant_token"; token: string }
  | { type: "apply_event"; eventType: string; data: unknown }
  | { type: "set_error"; error: string };

const INITIAL: State = {
  status: "idle",
  cart: null,
  participant: null,
  error: null,
};

function statusForCart(cart: GroupCart): GroupCartStatus {
  if (cart.status === "active") return "active";
  if (cart.status === "closed") return "fired";
  if (cart.status === "abandoned") return "abandoned";
  return "active";
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "reset":
      return INITIAL;
    case "set_status":
      return { ...state, status: action.status };
    case "set_cart":
      return {
        ...state,
        cart: action.cart,
        participant: action.participant ?? state.participant,
        status: statusForCart(action.cart),
        error: null,
      };
    case "patch_participant_token":
      if (!state.participant) return state;
      return {
        ...state,
        participant: { ...state.participant, participantToken: action.token },
      };
    case "apply_event":
      return applyEvent(state, action.eventType, action.data);
    case "set_error":
      return { ...state, status: "error", error: action.error };
  }
}

interface ParticipantJoined { participant: GroupCartParticipant; version: number }
interface ParticipantLeft { guestId: string; version: number }
interface ParticipantVerified { guestId: string; customerId: string; version: number }
interface LineAdded { line: GroupCart["draftLines"][number]; version: number }
interface LineUpdated { line: GroupCart["draftLines"][number]; version: number }
interface LineRemoved { lineId: string; version: number }
interface CartFired { orderId: string; firedLineIds: string[]; version: number }

function applyEvent(state: State, evType: string, raw: unknown): State {
  if (!state.cart) return state;
  const cart = state.cart;
  const data = raw as Record<string, unknown>;

  switch (evType) {
    case "participant.joined": {
      const d = data as unknown as ParticipantJoined;
      if (cart.participants.some((p) => p.guestId === d.participant.guestId)) {
        // Idempotent — host echo of own join, etc.
        return { ...state, cart: { ...cart, version: d.version } };
      }
      return {
        ...state,
        cart: {
          ...cart,
          participants: [...cart.participants, d.participant],
          version: d.version,
        },
      };
    }
    case "participant.left": {
      const d = data as unknown as ParticipantLeft;
      return {
        ...state,
        cart: {
          ...cart,
          participants: cart.participants.filter((p) => p.guestId !== d.guestId),
          version: d.version,
        },
      };
    }
    case "participant.verified": {
      const d = data as unknown as ParticipantVerified;
      return {
        ...state,
        cart: {
          ...cart,
          participants: cart.participants.map((p) =>
            p.guestId === d.guestId
              ? { ...p, verified: true, customerId: d.customerId }
              : p,
          ),
          version: d.version,
        },
      };
    }
    case "line.added": {
      const d = data as unknown as LineAdded;
      if (cart.draftLines.some((l) => l.lineId === d.line.lineId)) {
        return { ...state, cart: { ...cart, version: d.version } };
      }
      return {
        ...state,
        cart: {
          ...cart,
          draftLines: [...cart.draftLines, d.line],
          version: d.version,
        },
      };
    }
    case "line.updated": {
      const d = data as unknown as LineUpdated;
      return {
        ...state,
        cart: {
          ...cart,
          draftLines: cart.draftLines.map((l) =>
            l.lineId === d.line.lineId ? d.line : l,
          ),
          version: d.version,
        },
      };
    }
    case "line.removed": {
      const d = data as unknown as LineRemoved;
      return {
        ...state,
        cart: {
          ...cart,
          draftLines: cart.draftLines.filter((l) => l.lineId !== d.lineId),
          version: d.version,
        },
      };
    }
    case "cart.fired": {
      const d = data as unknown as CartFired;
      return {
        ...state,
        cart: { ...cart, draftLines: [], orderId: d.orderId, version: d.version, status: "closed" },
        status: "fired",
      };
    }
    case "cart.abandoned":
      return {
        ...state,
        cart: { ...cart, status: "abandoned" },
        status: "abandoned",
      };
    default:
      return state;
  }
}

interface CreateGroupOpts {
  displayName: string;
  seedLines?: AddLineInput[];
}

interface JoinOpts {
  displayName: string;
}

export interface GroupCartCtx {
  status: GroupCartStatus;
  cart: GroupCart | null;
  participant: StoredParticipant | null;
  isHost: boolean;
  myParticipant: GroupCartParticipant | null;
  error: string | null;

  createGroup(opts: CreateGroupOpts): Promise<void>;
  joinByCode(code: string, opts: JoinOpts): Promise<void>;
  joinById(groupCartId: string, opts: JoinOpts): Promise<void>;

  addLine(input: AddLineInput): Promise<void>;
  updateLine(lineId: string, patch: UpdateLinePatch): Promise<void>;
  removeLine(lineId: string): Promise<void>;

  fire(posId?: string | null): Promise<{ orderId: string }>;
  verifyMe(customerId: string): Promise<void>;
  leave(): Promise<void>;

  resetSession(): void;
}

const Ctx = createContext<GroupCartCtx | null>(null);

export function GroupCartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const { tableInfo } = useTable();
  const sseCloseRef = useRef<(() => void) | null>(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // 1) Hydrate from sessionStorage on mount.
  useEffect(() => {
    let cancelled = false;
    const stored = readParticipant();
    if (!stored) return;
    (async () => {
      try {
        const cart = await groupCartApi.get(stored.groupCartId, stored.participantToken);
        if (cancelled) return;
        dispatch({ type: "set_cart", cart, participant: stored });
      } catch (err) {
        // Token expired / cart gone — drop the stale entry.
        console.warn("[group-cart] hydrate failed, clearing", err);
        writeParticipant(null);
        if (!cancelled) dispatch({ type: "reset" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 2) Persist participant on every change. Only write *non-null* values —
  // the initial reducer state is `null`, and we don't want that initial mount
  // (or React.StrictMode's double-mount in dev) to clobber the participant
  // we just read in the hydrate effect above. Explicit clears live in
  // `leave()`, `resetSession()`, and the hydrate-failure branch.
  useEffect(() => {
    if (state.participant) writeParticipant(state.participant);
  }, [state.participant]);

  // 3) Open SSE while active.
  useEffect(() => {
    if (state.status !== "active" || !state.cart || !state.participant) return;
    const url = `${SSE_BASE}/v1/online-ordering/group-carts/${encodeURIComponent(state.cart.groupCartId)}/events`;
    const close = openSSE(
      url,
      state.participant.participantToken,
      {
        onSnapshot: (data) => dispatch({ type: "set_cart", cart: data as GroupCart }),
        onEvent: (type, data) => dispatch({ type: "apply_event", eventType: type, data }),
        onError: (err) => console.warn("[group-cart] SSE error", err),
      },
      // When SSE bypasses the API Gateway and hits Cloud Run directly, the
      // backend still needs X-API-KEY to resolve the tenant.
      { extraHeaders: { "X-API-KEY": getApiKey() } },
    );
    sseCloseRef.current = close;
    return () => {
      close();
      sseCloseRef.current = null;
    };
  }, [state.status, state.cart?.groupCartId, state.participant?.participantToken]);

  // --- Mutations ---

  const findGuestId = useCallback(
    (cart: GroupCart, displayName: string): string => {
      // The server stamps the new participant onto the response cart — find
      // the freshest matching displayName (last by joinedAt) so a re-creator
      // doesn't pick up an earlier participant.
      const matches = cart.participants
        .filter((p) => p.displayName === displayName)
        .sort((a, b) => (a.joinedAt < b.joinedAt ? 1 : -1));
      if (matches[0]) return matches[0].guestId;
      // Fall back to createdByGuestId — covers the create_or_join host case.
      return cart.createdByGuestId;
    },
    [],
  );

  const createGroup = useCallback(
    async ({ displayName, seedLines }: CreateGroupOpts) => {
      if (!tableInfo?.branchId || !tableInfo.tableId) {
        throw new Error("No table context for createGroup");
      }
      dispatch({ type: "set_status", status: "creating" });
      try {
        const r = await groupCartApi.createOrJoin({
          branchId: tableInfo.branchId,
          tableId: tableInfo.tableId,
          floorId: tableInfo.floorId ?? undefined,
          displayName,
          deviceFingerprint: deviceFingerprint(),
          seedLines,
        });
        const guestId = r.participant?.guestId ?? findGuestId(r.cart, displayName);
        dispatch({
          type: "set_cart",
          cart: r.cart,
          participant: {
            groupCartId: r.groupCartId,
            guestId,
            displayName,
            participantToken: r.participantToken,
          },
        });
        if (r.createdNew) {
          trackGroupCartCreated({
            groupCartId: r.groupCartId,
            branchId: r.cart.branchId,
            tableId: r.cart.tableId,
            seedLineCount: r.cart.draftLines.length,
          });
        } else {
          trackGroupCartJoined({
            groupCartId: r.groupCartId,
            via: "probe_redirect",
            participantCount: r.cart.participants.length,
          });
        }
      } catch (err) {
        dispatch({ type: "set_error", error: (err as Error).message });
        throw err;
      }
    },
    [tableInfo, findGuestId],
  );

  const joinByCode = useCallback(
    async (code: string, { displayName }: JoinOpts) => {
      dispatch({ type: "set_status", status: "joining" });
      try {
        const r = await groupCartApi.joinByCode(code, {
          displayName,
          deviceFingerprint: deviceFingerprint(),
        });
        const guestId = r.participant?.guestId ?? findGuestId(r.cart, displayName);
        dispatch({
          type: "set_cart",
          cart: r.cart,
          participant: {
            groupCartId: r.groupCartId,
            guestId,
            displayName,
            participantToken: r.participantToken,
          },
        });
        trackGroupCartJoined({
          groupCartId: r.groupCartId,
          via: "code",
          participantCount: r.cart.participants.length,
        });
      } catch (err) {
        dispatch({ type: "set_error", error: (err as Error).message });
        throw err;
      }
    },
    [findGuestId],
  );

  const joinById = useCallback(
    async (groupCartId: string, { displayName }: JoinOpts) => {
      dispatch({ type: "set_status", status: "joining" });
      try {
        const r = await groupCartApi.joinById(groupCartId, {
          displayName,
          deviceFingerprint: deviceFingerprint(),
        });
        const guestId = r.participant?.guestId ?? findGuestId(r.cart, displayName);
        dispatch({
          type: "set_cart",
          cart: r.cart,
          participant: {
            groupCartId: r.groupCartId,
            guestId,
            displayName,
            participantToken: r.participantToken,
          },
        });
        trackGroupCartJoined({
          groupCartId: r.groupCartId,
          via: "id",
          participantCount: r.cart.participants.length,
        });
      } catch (err) {
        dispatch({ type: "set_error", error: (err as Error).message });
        throw err;
      }
    },
    [findGuestId],
  );

  const requireActive = useCallback(() => {
    const s = stateRef.current;
    if (!s.cart || !s.participant) throw new Error("No active group cart");
    return { cart: s.cart, participant: s.participant };
  }, []);

  const resync = useCallback((err: GroupCartVersionConflict) => {
    dispatch({ type: "set_cart", cart: err.cart });
  }, []);

  const handleConflict = useCallback((err: unknown) => {
    if (err instanceof GroupCartVersionConflict) {
      dispatch({ type: "set_cart", cart: err.cart });
    }
    throw err;
  }, []);

  const addLine = useCallback(
    async (input: AddLineInput) => {
      const { cart, participant } = requireActive();
      try {
        const r = await withVersionRetry(
          cart.version,
          (v) =>
            groupCartApi.addLine(
              cart.groupCartId,
              v,
              input,
              participant.participantToken,
            ),
          { onConflict: resync },
        );
        dispatch({ type: "apply_event", eventType: "line.added", data: r });
      } catch (err) {
        handleConflict(err);
      }
    },
    [requireActive, handleConflict, resync],
  );

  // One write per line at a time, newest intent last (F31).
  const lineWrites = useRef(latestWins<UpdateLinePatch>());

  const sendLineUpdate = useCallback(
    async (lineId: string, patch: UpdateLinePatch) => {
      const { cart, participant } = requireActive();
      try {
        const r = await withVersionRetry(
          cart.version,
          (v) =>
            groupCartApi.updateLine(
              cart.groupCartId,
              v,
              lineId,
              patch,
              participant.participantToken,
            ),
          {
            onConflict: resync,
            canRetry: (c) => c.cart.draftLines.some((l) => l.lineId === lineId),
          },
        );
        // The PATCH response only carries the new version — re-fetch the line
        // from local state and rebuild it with the patch applied so SSE-late
        // viewers don't briefly diverge.
        const existing = cart.draftLines.find((l) => l.lineId === lineId);
        if (existing) {
          const merged = {
            ...existing,
            ...(patch.quantity != null ? { quantity: patch.quantity } : {}),
            ...(patch.notes !== undefined ? { notes: patch.notes ?? null } : {}),
            ...(patch.variantSelections != null
              ? { variantSelections: patch.variantSelections }
              : {}),
            ...(patch.modifierSelections != null
              ? { modifierSelections: patch.modifierSelections }
              : {}),
          };
          dispatch({
            type: "apply_event",
            eventType: "line.updated",
            data: { line: merged, version: r.version },
          });
        }
      } catch (err) {
        handleConflict(err);
      }
    },
    [requireActive, handleConflict, resync],
  );

  const updateLine = useCallback(
    (lineId: string, patch: UpdateLinePatch) =>
      lineWrites.current.run(lineId, patch, (p) => sendLineUpdate(lineId, p)),
    [sendLineUpdate],
  );

  const removeLine = useCallback(
    async (lineId: string) => {
      const { cart, participant } = requireActive();
      try {
        const r = await withVersionRetry(
          cart.version,
          (v) =>
            groupCartApi.removeLine(
              cart.groupCartId,
              v,
              lineId,
              participant.participantToken,
            ),
          {
            onConflict: resync,
            canRetry: (c) => c.cart.draftLines.some((l) => l.lineId === lineId),
          },
        );
        dispatch({
          type: "apply_event",
          eventType: "line.removed",
          data: { lineId, version: r.version },
        });
      } catch (err) {
        handleConflict(err);
      }
    },
    [requireActive, handleConflict, resync],
  );

  const fire = useCallback(async (posId?: string | null) => {
    const { cart, participant } = requireActive();
    try {
      const r = await withVersionRetry(
        cart.version,
        (v) =>
          groupCartApi.fire(cart.groupCartId, v, participant.participantToken, posId),
        { onConflict: resync, canRetry: canRefire },
      );
      dispatch({
        type: "apply_event",
        eventType: "cart.fired",
        data: { orderId: r.orderId, firedLineIds: r.firedLineIds, version: r.version },
      });
      trackGroupCartFired({
        groupCartId: cart.groupCartId,
        orderId: r.orderId,
        firedLineCount: r.firedLineIds.length,
        participantCount: cart.participants.length,
      });
      return { orderId: r.orderId };
    } catch (err) {
      handleConflict(err);
      throw err; // unreachable, satisfies TS
    }
  }, [requireActive, handleConflict, resync]);

  const verifyMe = useCallback(
    async (customerId: string) => {
      const { cart, participant } = requireActive();
      const r = await groupCartApi.verifyMe(
        cart.groupCartId,
        customerId,
        participant.participantToken,
      );
      dispatch({ type: "patch_participant_token", token: r.participantToken });
      // The server emits a `participant.verified` SSE event for everyone — we
      // intentionally don't dispatch it locally so we don't double-apply.
    },
    [requireActive],
  );

  const leave = useCallback(async () => {
    const { cart, participant } = requireActive();
    trackGroupCartAbandoned({
      groupCartId: cart.groupCartId,
      reason: "user_left",
    });
    try {
      await groupCartApi.leave(
        cart.groupCartId,
        cart.version,
        participant.participantToken,
      );
    } catch (err) {
      // 412 here is benign — somebody else mutated the cart between our last
      // version and the leave. Treat as success and let SSE reconcile.
      if (!(err instanceof GroupCartVersionConflict)) throw err;
    }
    sseCloseRef.current?.();
    sseCloseRef.current = null;
    writeParticipant(null);
    dispatch({ type: "reset" });
  }, [requireActive]);

  const resetSession = useCallback(() => {
    sseCloseRef.current?.();
    sseCloseRef.current = null;
    writeParticipant(null);
    dispatch({ type: "reset" });
  }, []);

  // --- Derived ---

  const myParticipant = useMemo<GroupCartParticipant | null>(() => {
    if (!state.cart || !state.participant) return null;
    return (
      state.cart.participants.find(
        (p) => p.guestId === state.participant!.guestId,
      ) ?? null
    );
  }, [state.cart, state.participant]);

  const isHost = !!(
    state.cart &&
    state.participant &&
    state.cart.createdByGuestId === state.participant.guestId
  );

  const value = useMemo<GroupCartCtx>(
    () => ({
      status: state.status,
      cart: state.cart,
      participant: state.participant,
      isHost,
      myParticipant,
      error: state.error,
      createGroup,
      joinByCode,
      joinById,
      addLine,
      updateLine,
      removeLine,
      fire,
      verifyMe,
      leave,
      resetSession,
    }),
    [
      state,
      isHost,
      myParticipant,
      createGroup,
      joinByCode,
      joinById,
      addLine,
      updateLine,
      removeLine,
      fire,
      verifyMe,
      leave,
      resetSession,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGroupCart(): GroupCartCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useGroupCart must be used inside <GroupCartProvider>");
  return ctx;
}
